import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Clock, Zap, Globe } from "lucide-react";

const TIMES_15 = (() => {
    const out = [];
    for (let h = 0; h < 24; h++) {
        for (const m of [0, 15, 30, 45]) {
            out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    return out;
})();

export default function NotificationsSection() {
    const [state, setState] = useState(null);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get("/workspaces/me/digest-defaults"),
            api.get("/workspaces/me/timezones"),
        ])
            .then(([{ data }, { data: tzData }]) => {
                setState(data);
                setZones(tzData?.timezones || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const patch = async (body, ok) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/workspaces/me/digest-defaults", body);
            setState(data);
            toast.success(ok || "Notification defaults saved");
        } catch (err) {
            toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setSaving(false);
        }
    };

    if (loading || !state) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-10 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="settings-notifications-panel">
            <div className="mb-6">
                <h2 className="font-display text-xl text-slate-900">Notifications</h2>
                <p className="text-slate-500 mt-1 text-sm">Workspace-wide defaults for how portal updates reach your clients. Individual clients can override these from their portal page.</p>
            </div>

            <div className="space-y-6">
                {/* Default email delivery */}
                <div>
                    <p className="text-sm font-medium text-slate-900 mb-3">Default email delivery</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <ModeCard
                            testid="settings-mode-immediate"
                            icon={<Zap className="w-4 h-4 text-[#C55A46]" />}
                            title="Immediate"
                            desc="Each eligible event triggers its own email right away."
                            selected={state.digest_default_mode === "immediate"}
                            disabled={saving}
                            onClick={() => patch({ digest_default_mode: "immediate" }, "Default set to Immediate")}
                        />
                        <ModeCard
                            testid="settings-mode-daily-digest"
                            icon={<Clock className="w-4 h-4 text-[#C55A46]" />}
                            title="Daily Digest"
                            desc="Group non-urgent updates into one professional email each morning."
                            selected={state.digest_default_mode === "daily_digest"}
                            disabled={saving}
                            onClick={() => patch({ digest_default_mode: "daily_digest" }, "Default set to Daily Digest")}
                        />
                    </div>
                </div>

                {/* Digest time */}
                <div>
                    <p className="text-sm font-medium text-slate-900 mb-3">Default Daily Digest time</p>
                    <div className="flex items-center gap-3">
                        <select
                            data-testid="settings-digest-time"
                            disabled={saving}
                            value={state.digest_default_time}
                            onChange={(e) => patch({ digest_default_time: e.target.value }, "Digest time saved")}
                            className="rounded-md border border-slate-200 bg-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C55A46]/30"
                        >
                            {TIMES_15.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                        <span className="text-xs text-slate-500">in the workspace timezone below</span>
                    </div>
                </div>

                {/* Timezone */}
                <div>
                    <p className="text-sm font-medium text-slate-900 mb-3 inline-flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#C55A46]" /> Workspace timezone
                    </p>
                    <select
                        data-testid="settings-timezone"
                        disabled={saving}
                        value={state.timezone}
                        onChange={(e) => patch({ timezone: e.target.value }, "Timezone saved")}
                        className="rounded-md border border-slate-200 bg-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C55A46]/30 min-w-[240px]"
                    >
                        {zones.map((z) => (
                            <option key={z} value={z}>{z}</option>
                        ))}
                        {/* Fallback if server value not in curated list */}
                        {!zones.includes(state.timezone) && <option value={state.timezone}>{state.timezone}</option>}
                    </select>
                    <p className="text-xs text-slate-500 mt-2">
                        The Daily Digest fires at <span className="font-medium text-slate-800">{state.digest_default_time} · {state.timezone}</span> unless a client has its own override.
                    </p>
                </div>

                <div className="pt-2 text-xs text-slate-400 border-t border-slate-100">
                    Approval requests and other urgent alerts always send immediately, regardless of these defaults.
                </div>
            </div>
        </div>
    );
}

function ModeCard({ icon, title, desc, selected, disabled, onClick, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            aria-pressed={selected}
            disabled={disabled}
            onClick={onClick}
            className={`text-left rounded-lg border p-4 transition ${selected ? "border-[#C55A46] bg-[#C55A46]/5 ring-1 ring-[#C55A46]/30" : "border-slate-200 hover:border-slate-300 bg-white"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5">{icon}</div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{title}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
                </div>
            </div>
        </button>
    );
}
